import { useId } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import TextField from '@/components/feedback/fields/TextField'
import LongTextField from '@/components/feedback/fields/LongTextField'
import ChoiceField from '@/components/feedback/fields/ChoiceField'
import MultiChoiceField from '@/components/feedback/fields/MultiChoiceField'
import DateField from '@/components/feedback/fields/DateField'
import StarField from '@/components/feedback/fields/StarField'
import { OTHER_PREFIX } from '@/utils/registrationForm'

const KINDS = {
  text: TextField,
  longtext: LongTextField,
  choice: ChoiceField,
  multichoice: MultiChoiceField,
  date: DateField,
  stars: StarField,
}

// Kinds whose control is a single native input that a <label htmlFor> can target.
const SINGLE_CONTROL_KINDS = new Set(['text', 'longtext', 'date'])

const isOtherEntry = (v) => typeof v === 'string' && v.startsWith(OTHER_PREFIX)

const OtherTextInput = ({ field, value, onChange, disabled }) => (
  <Input
    type="text"
    value={value.slice(OTHER_PREFIX.length)}
    onChange={(e) => onChange(`${OTHER_PREFIX}${e.target.value}`)}
    placeholder="Please specify"
    aria-label={`${field.label}: other`}
    disabled={disabled}
    className="mt-1"
  />
)

// Single choice + "Other": the Other radio lives inside the same radio group.
const ChoiceWithOther = ({ field, id, value, onChange, disabled }) => {
  const otherSelected = isOtherEntry(value)
  const otherId = `${id}-other`
  return (
    <div className="space-y-2">
      <ChoiceField
        field={field}
        id={id}
        value={otherSelected ? OTHER_PREFIX : value}
        onChange={onChange}
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value={OTHER_PREFIX} id={otherId} className="h-5 w-5 border-2" />
          <Label htmlFor={otherId} className="text-sm font-normal cursor-pointer">Other</Label>
        </div>
      </ChoiceField>
      {otherSelected && (
        <OtherTextInput field={field} value={value} onChange={onChange} disabled={disabled} />
      )}
    </div>
  )
}

// Multi choice + "Other": the Other entry is stored as one array item, `Other: <text>`.
const MultiChoiceWithOther = ({ field, id, value, onChange, disabled }) => {
  const selected = Array.isArray(value) ? value : []
  const otherEntry = selected.find(isOtherEntry)
  const otherId = `${id}-other`

  const toggleOther = (checked) => {
    const rest = selected.filter((v) => !isOtherEntry(v))
    onChange(checked ? [...rest, OTHER_PREFIX] : rest)
  }
  const changeOtherText = (next) => {
    onChange(selected.map((v) => (isOtherEntry(v) ? next : v)))
  }

  return (
    <div className="space-y-2">
      <MultiChoiceField field={field} id={id} value={selected} onChange={onChange} disabled={disabled}>
        <div className="flex items-center gap-2">
          <Checkbox
            id={otherId}
            checked={otherEntry !== undefined}
            onCheckedChange={(checked) => toggleOther(checked === true)}
            disabled={disabled}
            className="h-5 w-5 border-2"
          />
          <Label htmlFor={otherId} className="text-sm font-normal cursor-pointer">Other</Label>
        </div>
      </MultiChoiceField>
      {otherEntry !== undefined && (
        <OtherTextInput field={field} value={otherEntry} onChange={changeOtherText} disabled={disabled} />
      )}
    </div>
  )
}

const FieldControl = ({ field, id, value, onChange, disabled }) => {
  if (field.allowOther && field.kind === 'choice') {
    return <ChoiceWithOther field={field} id={id} value={value} onChange={onChange} disabled={disabled} />
  }
  if (field.allowOther && field.kind === 'multichoice') {
    return <MultiChoiceWithOther field={field} id={id} value={value} onChange={onChange} disabled={disabled} />
  }
  const Component = KINDS[field.kind]
  return <Component field={field} id={id} value={value} onChange={onChange} disabled={disabled} />
}

const RegistrationFormFields = ({ definition, answers, onChange, disabled = false }) => {
  const fields = definition?.fields || []
  // EventDetail renders this section twice (mobile + desktop), so every DOM id
  // carries a per-instance prefix to stay unique and keep labels bound to their own copy.
  const prefix = useId()

  return (
    <div className="space-y-5">
      {fields.map((field) => {
        if (!KINDS[field.kind]) return null

        const labelContent = (
          <>
            {field.label}
            {field.required && <span className="text-red-600" aria-hidden="true"> *</span>}
          </>
        )
        const labelClass = 'block text-sm font-medium text-gray-800'
        const controlId = `${prefix}-${field.id}`
        const labelId = `${controlId}-label`
        const grouped = !SINGLE_CONTROL_KINDS.has(field.kind)

        return (
          <div key={field.id} className="space-y-2">
            <div className="space-y-0.5">
              {grouped ? (
                <span id={labelId} className={labelClass}>{labelContent}</span>
              ) : (
                <label htmlFor={controlId} className={labelClass}>{labelContent}</label>
              )}
              {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
            </div>
            <div {...(grouped ? { role: 'group', 'aria-labelledby': labelId } : {})}>
              <FieldControl
                field={field}
                id={controlId}
                value={Object.prototype.hasOwnProperty.call(answers ?? {}, field.id) ? answers[field.id] : undefined}
                onChange={(next) => onChange(field.id, next)}
                disabled={disabled}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default RegistrationFormFields
